import { compare } from '../views/sayItBack.ts';
import { FRESH, MAX_ATTEMPTS, MAX_PLAYS, type Take } from './dictation.ts';

/**
 * The activity's unsent work, kept on the device so a reload or a closed tab
 * does not cost a learner the retelling they were writing (final review,
 * finding 5). `localStorage` rather than the server: nothing is the
 * learner's record until it is handed in (§5), and a per-device convenience
 * is what browser storage is for.
 */
export type Draft = { text: string; takes: Take[] };

/**
 * One draft per learner and passage, which `(book, first page)` names
 * (§10.1). The learner is in the key because a browser is shared between
 * accounts, and a draft is a learner's own writing: without it, the next
 * account to open the passage was handed the last one's retelling.
 */
export type DraftPlace = { learnerId: string; bookId: string; firstPage: number };

const PREFIX = 'tutor.listen.draft:';

export function draftKey({ learnerId, bookId, firstPage }: DraftPlace): string {
  return `${PREFIX}${learnerId}:${bookId}:${firstPage}`;
}

/**
 * A take is stored without its comparison, which is re-derived from the
 * sentence on the way back: `compare` is pure, and a stored one would be a
 * second copy of the sentence that could disagree with the passage.
 */
type StoredTake = { plays: number; attempts: number; typed: string; checked: boolean };

/** Every failure answers "no draft": `localStorage` throws in a private window. */
export function readDraft(
  place: DraftPlace,
  targets: readonly string[],
  storage: Storage | undefined = globalThis.localStorage,
): Draft | null {
  let raw: unknown;
  try {
    const s = storage?.getItem(draftKey(place));
    if (s === null || s === undefined) return null;
    raw = JSON.parse(s);
  } catch {
    return null;
  }
  if (typeof raw !== 'object' || raw === null || typeof (raw as Draft).text !== 'string') return null;
  const { text, takes } = raw as { text: string; takes?: unknown };
  // A passage whose dictation changed since the draft was kept: the takes
  // belong to other sentences, and only the writing still fits.
  if (!Array.isArray(takes) || takes.length !== targets.length) return { text, takes: targets.map(() => FRESH) };
  return { text, takes: takes.map((t, i) => restored(t, targets[i])) };
}

function restored(t: unknown, target: string): Take {
  const s = t as Partial<StoredTake> | null;
  if (
    typeof s !== 'object' ||
    s === null ||
    typeof s.plays !== 'number' ||
    typeof s.attempts !== 'number' ||
    typeof s.typed !== 'string'
  ) {
    return FRESH;
  }
  // Clamped: a reload must not be a way to more plays or attempts than §3.3 allows.
  const plays = Math.min(MAX_PLAYS, Math.max(0, Math.floor(s.plays)));
  const attempts = Math.min(MAX_ATTEMPTS, Math.max(0, Math.floor(s.attempts)));
  const result = s.checked === true && attempts > 0 ? compare(target, s.typed) : null;
  return { plays, attempts, typed: s.typed, result };
}

/**
 * An untouched activity is cleared rather than written, so a passage only
 * opened leaves nothing behind.
 */
export function writeDraft(
  place: DraftPlace,
  draft: Draft,
  storage: Storage | undefined = globalThis.localStorage,
): void {
  const untouched = draft.text === '' && draft.takes.every((t) => t.plays === 0 && t.attempts === 0 && t.typed === '');
  if (untouched) return clearDraft(place, storage);
  const takes: StoredTake[] = draft.takes.map((t) => ({
    plays: t.plays,
    attempts: t.attempts,
    typed: t.typed,
    checked: t.result !== null,
  }));
  try {
    storage?.setItem(draftKey(place), JSON.stringify({ text: draft.text, takes }));
  } catch {
    // Site data blocked or the quota full: the draft lives as long as the tab.
  }
}

export function clearDraft(place: DraftPlace, storage: Storage | undefined = globalThis.localStorage): void {
  try {
    storage?.removeItem(draftKey(place));
  } catch {
    // As `writeDraft`.
  }
}

/**
 * Every learner's drafts, on signing out or deleting the account: the device
 * may be handed to someone else next, and an unsent retelling is still the
 * learner's writing.
 */
export function clearAllDrafts(storage: Storage | undefined = globalThis.localStorage): void {
  try {
    if (!storage) return;
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i);
      if (k?.startsWith(PREFIX)) keys.push(k);
    }
    for (const k of keys) storage.removeItem(k);
  } catch {
    // As `writeDraft`.
  }
}
